import * as React from 'react';
import { Search } from 'lucide-react';
import { Input, type InputProps } from './input';

export interface SearchInputProps
  extends Omit<InputProps, 'type' | 'leftElement' | 'onChange' | 'value'> {
  value?: string;
  defaultValue?: string;
  onSearch: (query: string) => void;
  onChange?: (value: string) => void;
  debounceMs?: number;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, defaultValue = '', onSearch, onChange, debounceMs = 500, onClear, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const currentValue = value !== undefined ? value : internalValue;
    const timeoutRef = React.useRef<number>(undefined);

    React.useEffect(() => {
      if (value !== undefined) setInternalValue(value);
    }, [value]);

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setInternalValue(next);
      onChange?.(next);

      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => onSearch(next), debounceMs);
    };

    const handleClear = () => {
      window.clearTimeout(timeoutRef.current);
      setInternalValue('');
      onChange?.('');
      onSearch('');
      onClear?.();
    };

    return (
      <Input
        ref={ref}
        type="search"
        value={currentValue}
        onChange={handleChange}
        onClear={handleClear}
        leftElement={<Search className="size-icon-xs text-muted-foreground" />}
        {...props}
      />
    );
  },
);
SearchInput.displayName = 'SearchInput';

export { SearchInput };
